import { useEffect, useState, useCallback } from 'react';
import { planRowDate } from '../utils/planRowDate';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight, Plus, Check, X, RotateCcw, Copy, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { mealPlanAPI } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getTodayString, formatDateString, normalizeDate } from '../utils/dateUtils';

function WeeklyPlan() {
  var navigate = useNavigate();
  var [weekOffset, setWeekOffset] = useState(0);
  var [weekStart, setWeekStart] = useState('');
  var [weeklyPlans, setWeeklyPlans] = useState({});
  var [loading, setLoading] = useState(true);
  var [actionLoading, setActionLoading] = useState(null);
  var [copyMode, setCopyMode] = useState(null);

  var mealTypes = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
  var daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  var getWeekStart = useCallback(function(offset) {
    var today = new Date();
    var day = today.getDay();
    var diff = today.getDate() - day + (day === 0 ? -6 : 1) + (offset * 7);
    var monday = new Date(today);
    monday.setDate(diff);
    return formatDateString(monday);
  }, []);

  useEffect(function() {
    setWeekStart(getWeekStart(weekOffset));
  }, [weekOffset, getWeekStart]);

  var fetchWeeklyPlan = useCallback(function() {
    if (!weekStart) return;
    
    setLoading(true);
    mealPlanAPI.getWeeklyPlan(weekStart)
      .then(function(response) {
        var plans = response.data;
        var organized = {};

        for (var i = 0; i < plans.length; i++) {
          var plan = plans[i];
          if (!organized[plan.date]) {
            organized[plan.date] = {};
          }
          if (!organized[plan.date][plan.meal_type]) {
            organized[plan.date][plan.meal_type] = [];
          }
          organized[plan.date][plan.meal_type].push(plan);
        }
        
        // Each row already carries its authoritative `macros` from GET
        // /meal-plans/weekly; no per-recipe catalog lookups are needed.
        setWeeklyPlans(organized);
      })
      .catch(function(error) {
        console.error('Failed to fetch weekly plan:', error);
        toast.error('Failed to load weekly plan');
      })
      .finally(function() {
        setLoading(false);
      });
  }, [weekStart]);

  useEffect(function() {
    fetchWeeklyPlan();
  }, [fetchWeeklyPlan]);

  function getDateForDay(dayIndex) {
    if (!weekStart) return '';
    // normalizeDate parses "YYYY-MM-DD" as local midday — raw `new Date(str)`
    // shifts the whole week back one day in negative-UTC-offset timezones
    // (see AGENTS.md pre-launch audit, finding DATE-1), which also threw off
    // this screen's "today" highlighting.
    var start = normalizeDate(weekStart);
    var date = new Date(start);
    date.setDate(start.getDate() + dayIndex);
    return formatDateString(date);
  }

  function isToday(date) {
    return date === getTodayString();
  }

  function updateMealStatus(planId, newStatus) {
    setActionLoading(planId + '-' + newStatus);
    
    mealPlanAPI.updateMealPlan(planId, newStatus)
      .then(function() {
        toast.success('Meal ' + newStatus);
        fetchWeeklyPlan();
      })
      .catch(function() {
        toast.error('Failed to update');
      })
      .finally(function() {
        setActionLoading(null);
      });
  }

  function removeMeal(planId) {
    setActionLoading(planId + '-remove');
    
    mealPlanAPI.deleteMealPlan(planId)
      .then(function() {
        toast.success('Meal removed');
        fetchWeeklyPlan();
      })
      .catch(function() {
        toast.error('Failed to remove');
      })
      .finally(function() {
        setActionLoading(null);
      });
  }

  function startCopy(plan) {
    setCopyMode(plan);
    toast.info('Select a day to copy this meal');
  }

  function copyMealToDate(targetDate, mealTypeToCopy) {
    if (!copyMode) return;
    
    mealPlanAPI.addMealPlan({
      date: targetDate,
      meal_type: mealTypeToCopy || copyMode.meal_type,
      meal_id: copyMode.meal_id,
      meal_name: copyMode.meal_name,
      source: 'copied'
    })
    .then(function() {
      toast.success('Meal copied!');
      setCopyMode(null);
      fetchWeeklyPlan();
    })
    .catch(function() {
      toast.error('Failed to copy meal');
    });
  }

  function getWeekRange() {
    if (!weekStart) return '';
    var start = normalizeDate(weekStart);
    var end = new Date(start);
    end.setDate(start.getDate() + 6);
    var opts = { month: 'short', day: 'numeric' };
    return start.toLocaleDateString('en-US', opts) + ' - ' + end.toLocaleDateString('en-US', opts);
  }

  function getStatusColor(status) {
    if (status === 'completed') return 'bg-accent/20 text-accent border-accent/30';
    if (status === 'skipped') return 'bg-muted text-muted-foreground border-border/30';
    return 'bg-secondary/30 border-border/20';
  }

  function goBack() {
    navigate(-1);
  }

  function prevWeek() {
    setWeekOffset(weekOffset - 1);
  }

  function nextWeek() {
    setWeekOffset(weekOffset + 1);
  }

  function cancelCopy() {
    setCopyMode(null);
  }

  function goToBrowseMeals() {
    navigate('/meals');
  }

  function goToMealView(mealId, plan) {
    // Pass plan context so MealView can hydrate any saved edits for this
    // logged/planned instance (keeps edit-persistence consistent with the
    // Plan/Home/Progress flows).
    var params = new URLSearchParams();
    params.set('return', '/plan/weekly');
    if (plan) {
      if (planRowDate(plan)) params.set('date', planRowDate(plan));
      if (plan.plan_id) params.set('planId', plan.plan_id);
      if (plan.status) params.set('status', plan.status);
      if (plan.meal_type) params.set('type', plan.meal_type);
      if (plan.scale_factor && plan.scale_factor !== 1.0) {
        params.set('scaleFactor', plan.scale_factor);
        if (plan.scale_label) params.set('scaleLabel', plan.scale_label);
      }
    }
    navigate('/meal-view/' + mealId + '?' + params.toString());
  }

  function goToAddMeal(mealTypeLower, date) {
    navigate('/meals?type=' + mealTypeLower + '&date=' + date);
  }

  // Pre-render day cards
  var dayCards = [];
  for (var dayIdx = 0; dayIdx < daysOfWeek.length; dayIdx++) {
    var day = daysOfWeek[dayIdx];
    var date = getDateForDay(dayIdx);
    var dayPlans = weeklyPlans[date] || {};
    var today = isToday(date);
    
    var allDayPlans = [];
    for (var mt = 0; mt < mealTypes.length; mt++) {
      var plans = dayPlans[mealTypes[mt]] || [];
      for (var p = 0; p < plans.length; p++) {
        allDayPlans.push(plans[p]);
      }
    }
    var completedCount = allDayPlans.filter(function(pl) { return pl.status === 'completed'; }).length;
    var totalCount = allDayPlans.length;
    
    // Pre-render meal slots for this day
    var mealSlots = [];
    for (var typeIdx = 0; typeIdx < mealTypes.length; typeIdx++) {
      var mealType = mealTypes[typeIdx];
      var mealTypePlans = dayPlans[mealType] || [];
      var mealTypeLower = mealType.toLowerCase();
      
      var slotContent;
      if (mealTypePlans.length > 0) {
        var planCards = [];
        for (var planIdx = 0; planIdx < mealTypePlans.length; planIdx++) {
          var plan = mealTypePlans[planIdx];
          
          // Pre-render action buttons for this plan
          var actionButtons;
          if (plan.status === 'pending') {
            actionButtons = (
              <div className="flex gap-1 mt-2">
                <button
                  onClick={updateMealStatus.bind(null, plan.plan_id, 'completed')}
                  disabled={actionLoading === plan.plan_id + '-completed'}
                  className="p-1 rounded bg-accent/20 text-accent hover:bg-accent/30 transition-colors"
                  title="Log as eaten"
                  data-testid={'complete-' + plan.plan_id}
                >
                  <Check size={12} />
                </button>
                <button
                  onClick={updateMealStatus.bind(null, plan.plan_id, 'skipped')}
                  disabled={actionLoading === plan.plan_id + '-skipped'}
                  className="p-1 rounded bg-secondary hover:bg-secondary/80 transition-colors"
                  title="Skip"
                  data-testid={'skip-' + plan.plan_id}
                >
                  <X size={12} />
                </button>
                <button
                  onClick={startCopy.bind(null, plan)}
                  className="p-1 rounded bg-secondary hover:bg-secondary/80 transition-colors"
                  title="Copy to another day"
                  data-testid={'copy-' + plan.plan_id}
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={removeMeal.bind(null, plan.plan_id)}
                  disabled={actionLoading === plan.plan_id + '-remove'}
                  className="p-1 rounded text-destructive hover:bg-destructive/10 transition-colors"
                  title="Remove"
                  data-testid={'remove-' + plan.plan_id}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          } else {
            actionButtons = (
              <div className="flex gap-1 mt-2">
                <button
                  onClick={updateMealStatus.bind(null, plan.plan_id, 'pending')}
                  disabled={actionLoading === plan.plan_id + '-pending'}
                  className="p-1 rounded bg-secondary hover:bg-secondary/80 transition-colors"
                  title="Reset"
                  data-testid={'reset-' + plan.plan_id}
                >
                  <RotateCcw size={12} />
                </button>
                <button
                  onClick={startCopy.bind(null, plan)}
                  className="p-1 rounded bg-secondary hover:bg-secondary/80 transition-colors"
                  title="Copy to another day"
                  data-testid={'copy-' + plan.plan_id}
                >
                  <Copy size={12} />
                </button>
                <button
                  onClick={removeMeal.bind(null, plan.plan_id)}
                  disabled={actionLoading === plan.plan_id + '-remove'}
                  className="p-1 rounded text-destructive hover:bg-destructive/10 transition-colors"
                  title="Remove"
                  data-testid={'remove-' + plan.plan_id}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          }
          
          planCards.push(
            <div 
              key={plan.plan_id}
              data-testid={'meal-' + plan.plan_id}
              className={'rounded-lg p-2 border transition-all ' + getStatusColor(plan.status)}
            >
              <p 
                className="text-sm font-medium truncate cursor-pointer hover:text-accent"
                onClick={goToMealView.bind(null, plan.meal_id, plan)}
              >
                {plan.meal_name}
              </p>
              {plan.macros && (
                // The saved row's own authoritative macros (GET
                // /meal-plans/weekly `macros`), never the catalog recipe's.
                <p className="text-xs text-muted-foreground">{Math.round(plan.macros.calories || 0)} cal</p>
              )}
              {actionButtons}
            </div>
          );
        }
        slotContent = planCards;
      } else {
        slotContent = (
          <button
            onClick={copyMode ? copyMealToDate.bind(null, date, mealTypeLower) : goToAddMeal.bind(null, mealTypeLower, date)}
            data-testid={'add-' + day.toLowerCase() + '-' + mealTypeLower}
            className={'w-full p-3 rounded-lg border-2 border-dashed text-center transition-all ' + (copyMode ? 'border-accent/50 bg-accent/5 hover:bg-accent/10' : 'border-border/30 hover:border-accent/30 hover:bg-secondary/20')}
          >
            <Plus size={14} className={'mx-auto ' + (copyMode ? 'text-accent' : 'text-muted-foreground')} />
            <p className="text-xs text-muted-foreground mt-1">{copyMode ? 'Paste here' : 'Add'}</p>
          </button>
        );
      }
      
      mealSlots.push(
        <div key={mealType} className="space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wide px-1">{mealType}</p>
          {slotContent}
        </div>
      );
    }
    
    dayCards.push(
      <motion.div 
        key={day} 
        initial={{ opacity: 0, y: 0 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: dayIdx * 0.03 }}
        data-testid={'day-' + day.toLowerCase()}
        className={'rounded-xl border shadow-sm overflow-hidden ' + (today ? 'border-accent/50 bg-accent/5' : 'border-border/40 bg-card')}
      >
        <div className={'px-4 py-3 flex items-center justify-between ' + (today ? 'bg-accent/10' : 'bg-secondary/20')}>
          <div className="flex items-center gap-3">
            <div className={'w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ' + (today ? 'bg-accent text-white' : 'bg-secondary')}>
              {normalizeDate(date).getDate()}
            </div>
            <div>
              <p className={'font-heading font-semibold ' + (today ? 'text-accent' : '')}>{day}</p>
              <p className="text-xs text-muted-foreground">
                {completedCount}/{totalCount} meals logged
              </p>
            </div>
          </div>
        </div>
        <div className="p-3 grid grid-cols-2 gap-2">
          {mealSlots}
        </div>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <Button 
              onClick={goBack} 
              variant="ghost" 
              size="icon" 
              className="rounded-full h-9 w-9"
              data-testid="back-btn"
            >
              <ArrowLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="font-heading font-bold text-xl">Weekly Plan</h1>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <Button 
              onClick={prevWeek}
              variant="ghost" 
              size="sm"
              className="rounded-full"
              data-testid="prev-week-btn"
            >
              <ChevronLeft size={18} />
            </Button>
            
            <div className="text-center">
              <p className="font-heading font-semibold text-sm" data-testid="week-range">{getWeekRange()}</p>
              {weekOffset === 0 && <p className="text-xs text-accent">This Week</p>}
              {weekOffset === 1 && <p className="text-xs text-muted-foreground">Next Week</p>}
              {weekOffset === -1 && <p className="text-xs text-muted-foreground">Last Week</p>}
            </div>
            
            <Button 
              onClick={nextWeek}
              variant="ghost" 
              size="sm"
              className="rounded-full"
              data-testid="next-week-btn"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {copyMode && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mx-4 mt-4 p-3 bg-accent/10 border border-accent/30 rounded-lg flex items-center justify-between"
          >
            <div>
              <p className="text-sm font-medium">Copying: {copyMode.meal_name}</p>
              <p className="text-xs text-muted-foreground">Tap an empty slot to paste</p>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={cancelCopy}
              className="rounded-full"
            >
              <X size={16} />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {dayCards}
          
          <div className="pt-4 space-y-3">
            <Button
              onClick={goToBrowseMeals}
              className="w-full h-12 rounded-full bg-accent text-white font-semibold hover:bg-accent/90"
              data-testid="browse-meals-btn"
            >
              <Plus size={18} className="mr-2" />
              Browse Meals
            </Button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

export default WeeklyPlan;
